import { observer } from 'mobx-react-lite';
import * as React from 'react';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import {
    getOptionalRecipientWellViewState,
    getRequiredRecipientWellViewState,
} from 'owa-calendar-compose-form-store/lib/utils/getRecipientWellViewState';
import { ControlIcons } from 'owa-control-icons';
import { shouldShowCompactReadOnlyWell } from 'owa-calendar-compose-form-store/lib/selectors/attendeeRowUtils';
import { useComputed } from 'owa-react-hooks/lib/useComputed';
import loc from 'owa-localize';
import {
    requiredWellLabel,
    optionalWellLabel,
    cannotChangeAttendeesLabelForFromThisDateForward,
    editSeriesLabel,
} from './AttendeeRows.locstring.json';
import { getComposeStore } from 'owa-calendar-compose-store-manager';
import CompactRecipientWell from 'owa-readonly-recipient-well/lib/components/CompactRecipientWell';
import ReadOnlyCompactRecipientWell from 'owa-readonly-recipient-well/lib/components/ReadOnlyCompactRecipientWell';
import type ReadOnlyRecipientViewState from 'owa-recipient-types/lib/types/ReadOnlyRecipientViewState';
import RecipientType from 'owa-recipient-types/lib/types/RecipientType';
import type AttendeeType from 'owa-service/lib/contract/AttendeeType';
import { Link } from '@fluentui/react/lib/Link';
import { lazyShowFullComposeInEditMode } from 'owa-calendar-compose-form-store';
import {
    setIsCompactRecipientWellExpanded,
    switchToReadWriteRecipientWell,
    updateOpenedForm,
} from 'owa-calendar-compose-form-actions';
import generateBorderStyle from 'owa-calendar-forms-common/lib/utils/generateBorderStyle';
import EventScope from 'owa-service/lib/contract/EventScope';
import getFullComposeViewState from 'owa-calendar-compose-form-store/lib/selectors/getFullComposeViewState';
import ComposeFormRow from './ComposeFormRow';

const styles = require('./AttendeeRow.scss');
const classNames = require('classnames/bind').bind(styles);

export interface CompactAttendeeRowProps {
    colorScheme: ColorScheme;
}

const CompactAttendeeRow: React.FunctionComponent<CompactAttendeeRowProps> = props => {
    /**
     * These requiredRecipientWellViewState are temporary until VSO 30418 and VSO 35590 are addressed.
     * They are created here to keep the well in sync with the event when attendees are added from external sources.
     */
    const requiredRecipientWellViewState = useComputed(getRequiredRecipientWellViewState);
    /**
     * These optionalRecipientWellViewState are temporary until VSO 30418 and VSO 35590 are addressed.
     * They are created here to keep the well in sync with the event when attendees are added from external sources.
     */
    const optionalRecipientWellViewState = useComputed(getOptionalRecipientWellViewState);
    const requiredReadOnlyRecipientWellViewStates = useComputed(
        (): ReadOnlyRecipientViewState[] => {
            const event = getComposeStore().event;
            return event.RequiredAttendees.map((attendee: AttendeeType) => ({
                email: attendee.Mailbox,
            }));
        }
    );
    const optionalReadOnlyRecipientWellViewStates = useComputed(
        (): ReadOnlyRecipientViewState[] => {
            const event = getComposeStore().event;
            return event.OptionalAttendees.map((attendee: AttendeeType) => ({
                email: attendee.Mailbox,
            }));
        }
    );

    let recipientWell;
    let borderStyle;
    if (shouldShowCompactReadOnlyWell()) {
        // If we are updating the series 'From this date forward' and it is NOT an appointment, then we do not support changing attendees.
        // So, show just read only well instead.
        const fullComposeViewState = getFullComposeViewState();
        const isCompactRecipientWellExpanded: boolean =
            !!fullComposeViewState && fullComposeViewState.isCompactRecipientWellExpanded;
        recipientWell = (
            <>
                <ReadOnlyCompactRecipientWell
                    toRecipients={requiredReadOnlyRecipientWellViewStates.get()}
                    ccRecipients={optionalReadOnlyRecipientWellViewStates.get()}
                    bccRecipients={null}
                    toRecipientsWellLabelTextOverride={loc(requiredWellLabel)}
                    ccRecipientsWellLabelTextOverride={loc(optionalWellLabel)}
                    className={styles.compactWell}
                    onTruncatedStateChanged={setIsCompactRecipientWellExpanded}
                />
                {isCompactRecipientWellExpanded && (
                    <div className={styles.recipientWellFtdfLabel}>
                        {loc(cannotChangeAttendeesLabelForFromThisDateForward)}
                        {
                            <Link onClick={onEditSeriesClicked} className={styles.ftdfLabelLink}>
                                {loc(editSeriesLabel)}
                            </Link>
                        }
                    </div>
                )}
            </>
        );
    } else {
        const { colorScheme } = props;
        const borderColorStyle = generateBorderStyle(colorScheme);
        borderStyle = borderColorStyle.bottomBorder;

        recipientWell = (
            <CompactRecipientWell
                toRecipientWell={requiredRecipientWellViewState.get()}
                ccRecipientWell={optionalRecipientWellViewState.get()}
                recipientType={RecipientType.None}
                onSwitchToReadWriteWell={switchToReadWriteRecipientWell}
            />
        );
    }

    return (
        <ComposeFormRow
            iconName={ControlIcons.Contact}
            containerClassName={classNames(styles.compactRecipientWell, borderStyle)}
            iconContainerClassName={styles.iconContainer}>
            {recipientWell}
        </ComposeFormRow>
    );
};

export default observer(CompactAttendeeRow);

function onEditSeriesClicked() {
    const fullComposeViewState = getFullComposeViewState();
    const viewType = fullComposeViewState.viewType;
    const { event } = getComposeStore();

    // Set openedForm = null so that FullComposeForm is unmounted from view.
    // We need to unmount FullCompose from view since we need the lifecycle functions related to addins
    // to get called in the correct order for the re-opened compose form in lazyShowFullComposeInEditMode
    updateOpenedForm(null);

    lazyShowFullComposeInEditMode.importAndExecute(
        { ItemId: event.ItemId, ParentFolderId: event.ParentFolderId },
        EventScope.AllInstancesInSeries,
        false /* itemHasUpdates */,
        'FTDFEditSeriesLink',
        viewType,
        null /* extractionSourceId */
    );
}
